import type { OMSConfig, StrategyEvaluator, SignalResult, StrategyContext } from '../types';
import { extractSpotPrice } from '../../../lib/snapshot-search';
import { ensureFlatOpts } from '../../analysis/trend-evaluator';

export const evaluateThetaScalp: StrategyEvaluator = (marketData: any, currentUnixTime: number, config: OMSConfig, context: StrategyContext): SignalResult[] | null => {
    if (!marketData) return null;

    const dateObj = new Date(currentUnixTime);
    const currentHHmm = `${dateObj.getHours().toString().padStart(2, '0')}:${dateObj.getMinutes().toString().padStart(2, '0')}`;
    
    // Strictly activates between 13:30 and 15:00
    if (currentHHmm < '13:30' || currentHHmm > '15:00') {
        return null;
    }

    const { symbol, expiry, gap } = context;
    const spot = extractSpotPrice(marketData) || 0;
    if (spot <= 0) return null;
    
    const atmStrike = Math.round(spot / gap) * gap;

    const liveRawOptsArr = context.chain || marketData.options || marketData.optionChain || [];
    const liveFlatOpts = ensureFlatOpts(liveRawOptsArr);

    const baseRawOptsArr = context.baseSnapshot?.data?.[symbol]?.[expiry] || [];
    const baseFlatOpts = ensureFlatOpts(baseRawOptsArr);

    if (baseFlatOpts.length === 0) return null;

    const signals: SignalResult[] = [];
    const minDistance = gap * 4; // At least 4 strikes away (Far OTM)

    for (const liveRow of liveFlatOpts) {
        // Only look at Far OTM
        if (liveRow.type === 'CE' && liveRow.strike < atmStrike + minDistance) continue;
        if (liveRow.type === 'PE' && liveRow.strike > atmStrike - minDistance) continue;

        const baseRow = baseFlatOpts.find((r: any) => r.strike === liveRow.strike && r.type === liveRow.type);
        if (!baseRow) continue;

        const currentLtp = liveRow.ltp || liveRow.close || liveRow.price || 0;
        const baseLtp = baseRow.ltp || baseRow.close || baseRow.price || 0;

        // Premium Spike Logic: If it spiked > 50% compared to morning AND still has enough premium to be worth selling (e.g. > 5)
        if (currentLtp > 5 && baseLtp > 0 && currentLtp >= baseLtp * 1.5) {
            signals.push({
                trade_type: liveRow.type === 'CE' ? 'BEAR' : 'BULL', // Sell CE = BEAR, Sell PE = BULL
                strike: liveRow.strike,
                option_type: liveRow.type,
                entry_price: currentLtp,
                remarks: `Theta Scalp: Spike detected on ${liveRow.strike} ${liveRow.type} (${baseLtp.toFixed(1)} -> ${currentLtp.toFixed(1)})`,
                exact_match: true
            });
        }
    }

    if (signals.length > 0) {
        // Return up to 4 legs max to prevent over-trading
        return signals.slice(0, 4);
    }

    return null;
};
